import React from "react";

import trello, { type TrelloStreamIntegrationDetails } from ".";
import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { commitLocalUpdate } from "relay-runtime";

import { useSyncedPersistentStateWithOverride } from "~/actionItems/hooks/useSyncedPersistentState";
import { TrelloLogo } from "~/integrations/trello/TrelloLogo";
import { base64Encode } from "~/lib/base64";
import { environment } from "~/models";
import {
	type ActionItemIntegrationCardProps,
	connectStreamIntegration,
	type IntegrationConfigProps,
	StreamSettingsIntegrationCard,
} from "~/stream/StreamSettings/ActionItemIntegrations";
import { Checkbox } from "~/ui";
import { Button } from "~/ui";
import Tippy from "~/ui/Tooltip";

import { getIntegrationForKey } from "../actionItemIntegration";
import { BoardSelector } from "./selectors/TrelloBoardSelector";
import { ListSelector } from "./selectors/TrelloListSelector";
import { OrganizationSelector } from "./selectors/TrelloOrganizationSelector";
import { trelloStore } from "./store";

import styles from "~/integrations/trello/css/styles.module.css";

export function TrelloStreamSettingsIntegrationCard({ viewer, onClickIntegration }: ActionItemIntegrationCardProps) {
	const onClick = React.useCallback(() => {
		onClickIntegration(getIntegrationForKey("TRELLO"));
	}, [onClickIntegration]);

	return <StreamSettingsIntegrationCard integration={trello} onClickIntegration={onClick} viewer={viewer} />;
}

export function TrelloStreamSettingsConfigView({
	integration,
	stream,
	onFinish,
	onCancel,
	integrationDetails,
}: IntegrationConfigProps<TrelloStreamIntegrationDetails>) {
	const [organizationId, setOrganizationId] = useSyncedPersistentStateWithOverride<string | undefined>(
		trelloStore,
		"organizationId",
		integrationDetails?.organizationId,
	);
	const [boardId, setBoardId] = useSyncedPersistentStateWithOverride<string | undefined>(
		trelloStore,
		"boardId",
		integrationDetails?.externalBoardId,
	);
	const [listId, setListId] = useSyncedPersistentStateWithOverride<string | undefined>(
		trelloStore,
		"listId",
		integrationDetails?.externalListId,
	);
	const [uncheckedListId, setUncheckedListId] = useSyncedPersistentStateWithOverride<string | undefined>(
		trelloStore,
		"uncheckedListId",
		integrationDetails?.uncheckedListId === null ? undefined : integrationDetails?.uncheckedListId,
	);
	const [doneListId, setDoneListId] = useSyncedPersistentStateWithOverride<string | undefined>(
		trelloStore,
		"doneListId",
		integrationDetails?.doneListId === null ? undefined : integrationDetails?.doneListId,
	);
	const [includeAiGenerated, setIncludeAiGenerated] = React.useState<boolean>(
		integrationDetails?.includeAiGenerated ?? true,
	);

	const handleIncludeAiGeneratedChange = React.useCallback((checked: boolean) => {
		setIncludeAiGenerated(checked);
	}, []);

	const submit = React.useCallback(async () => {
		if (!listId || !boardId || !stream || !stream.id) {
			throw new Error("One or more fields are invalid");
		}

		commitLocalUpdate(environment, store => {
			const relayId = base64Encode(`TrelloBoardNode:${boardId}`);
			const boardNode = store.get(relayId);
			/* eslint-disable camelcase */
			const destinationInfo = {
				list_id: listId,
				board_id: boardId,
				board_name: boardNode?.getValue("name"),
				unchecked_list_id: uncheckedListId,
				done_list_id: doneListId,
				include_ai_generated: includeAiGenerated,
			};

			/* eslint-enable camelcase */
			connectStreamIntegration("TRELLO", destinationInfo, stream.id).then(() => {
				onFinish(integration);
			});
		});
	}, [listId, boardId, uncheckedListId, doneListId, includeAiGenerated, stream, integration, onFinish]);

	return (
		<>
			<TrelloLogo style={{ width: "auto", height: "24px" }} />
			<div className={styles.settingsTitle}>Select a Trello Board</div>
			<p>
				Sync action items between the note series <b>{stream?.displayName}</b> and Trello.
			</p>
			<div className={styles.settingsContainer}>
				<div className={styles.organization}>
					<OrganizationSelector organizationId={organizationId} setOrganizationId={setOrganizationId} />
				</div>
				<div className={styles.board}>
					<BoardSelector organizationId={organizationId} boardId={boardId} setBoardId={setBoardId} />
				</div>
				<div className={styles.list}>
					<ListSelector boardId={boardId} listId={listId} setListId={setListId} />
				</div>
				<div className={styles.unchecked}>
					<div className={styles.settingsLabel}>
						Unchecked list
						<Tippy
							placement="top"
							className={styles.toolTip}
							content={"When you uncheck series, select the list they will be moved to in Trello"}
						>
							<span className={styles.helpPopup}>
								<FontAwesomeIcon icon={faInfoCircle} />
							</span>
						</Tippy>
					</div>
					<ListSelector boardId={boardId} listId={uncheckedListId} setListId={setUncheckedListId} />
				</div>
				<div className={styles.done}>
					<div className={styles.settingsLabel}>
						Done list
						<Tippy
							placement="top"
							className={styles.toolTip}
							content={"When you check series, select the list they will be moved to in Trello"}
						>
							<span className={styles.helpPopup}>
								<FontAwesomeIcon icon={faInfoCircle} />
							</span>
						</Tippy>
					</div>
					<ListSelector boardId={boardId} listId={doneListId} setListId={setDoneListId} />
				</div>
			</div>
			<div className={styles.toggleOption}>
				<Checkbox
					id="include-ai-generated"
					checked={includeAiGenerated}
					onCheckedChange={handleIncludeAiGeneratedChange}
				>
					Include AI-generated action items
				</Checkbox>
				<span className={styles.toggleDescription}>
					When enabled, AI-generated action items will be sent to this integration.
				</span>
			</div>
			<p>
				Action items <b>created after connecting</b> will be sent to Trello.
			</p>
			<div className={styles.buttonRow}>
				<Button size="normal" onClick={onCancel}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={submit} disabled={!listId}>
					Connect
				</Button>
			</div>
		</>
	);
}
